import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  GetProductResponse,
  ListProductsResponse,
  PaginatedResponse,
  PaginationParams,
  ProductOptions,
  ProductResource,
} from "@prequel/import-react";

import { importClient } from "./sdk/client";

import { type PageOptions, useAllPages, usePage } from "@/api/pagination";
import { useToast } from "@/providers/ToastProvider";

export type { ProductOptions, ProductResource } from "@prequel/import-react";

// The `all` product is created by the system for every org and grants access to everything.
export const SYSTEM_PRODUCT_NAME = "all";

const routes = {
  list: "/import/products",
  get: (id: string) => `/import/products/${id}`,
  create: "/import/products",
};

const keys = { all: ["import", "products"] as const };

export const listProductsPage = (params: PaginationParams): Promise<PaginatedResponse<ProductResource[]>> =>
  importClient.getPaginated<ListProductsResponse>(routes.list, params).then((res) => ({
    data: res.data.products,
    has_next: res.has_next,
    next_url: res.next_url,
  }));

export function useListProducts(options?: PageOptions) {
  return usePage<ProductResource>([...keys.all], listProductsPage, options);
}

export function useAllImportProducts() {
  return useAllPages<ProductResource>([...keys.all], listProductsPage);
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: [...keys.all, id],
    queryFn: () => importClient.get<GetProductResponse>(routes.get(id)).then((data) => data.product),
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (product: ProductOptions) =>
      importClient.post<GetProductResponse>(routes.create, { product }).then((data) => data.product),
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Product created successfully!" });
      navigate(`/import/destinations/products/${product.id}`);
    },
    // no onError: QueryProvider's mutationCache raises the error toast globally
  });
}
